/**
 * Residents API — all backend calls for the Residents tab.
 *
 * Each function maps directly to a V1 endpoint / stored procedure
 * but uses the new V2 api client for cleaner code.
 *
 * Endpoints & payloads are identical to V1 for full backend compatibility.
 */

import { apiFetch, apiTransaction } from '../../../../services/api.js';
import { NURSE_CALL_CATEGORY_FILTER } from '../../../../utils/deviceCategories.js';

// Re-export so the Residents page can fetch wandering devices from the
// same API surface as the other residents-related calls.
export { fetchWanderingDevices } from '../../../devices/api/devicesApi.js';
// Pending EHR move-in tasks, for the duplicate check on Add Resident.
export { fetchStagedTasks } from '../../../tasks/ehr/api/tasksEhrApi.js';

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/**
 * Fetch all residents (with devices) for a campus.
 * V1 procedure: sp_dev_getallresidentsanddevicesbycampusid
 *
 * @param {number} campusId
 * @param {boolean} [readOnly=true] — false on first load to bust cache
 * @returns {Promise<Array>}
 */
export function fetchResidents(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'sp_dev_getallresidentsanddevicesbycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly,
  });
}

export function fetchInovonicsLocations(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'getinovonicslocations',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly,
  });
}

/**
 * Fetch all areas for a campus.
 * V1 procedure: sp_dev_getareasbycampusid
 *
 * @param {number} campusId
 * @returns {Promise<Array>}
 */
export function fetchAreas(campusId) {
  return apiFetch({
    procedure: 'sp_dev_getareasbycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/**
 * Fetch available devices for assignment (Inovonics devices).
 * V1 procedure: sp_dev_getInovonicsDevices
 *
 * @param {number} campusId
 * @returns {Promise<Array>}
 */
export function fetchDevices(campusId) {
  return apiFetch({
    procedure: 'sp_dev_getInovonicsDevices',
    reqType: 'stored',
    payload: {
      device_filter: '',
      device_category_filter: NURSE_CALL_CATEGORY_FILTER,
      campus_id: campusId,
    },
    readOnly: true,
  });
}

/**
 * Fetch simple group list (id + name) for the resident form group selector.
 * V1 table: core_resident_groups (fetchbyparameter)
 *
 * @param {number} campusId
 * @returns {Promise<Array>}
 */
export function fetchGroupList(campusId) {
  return apiFetch({
    procedure: 'fetchbyparameter',
    reqType: 'direct',
    table: 'core_resident_groups',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * Add a new resident.
 * V1 TransType: resident_add
 *
 * @param {object} data — Resident form data
 * @param {object} session — { campusId, userId, clientId, vendor_ids }
 * @returns {Promise<[boolean, string?]>}
 */
export function addResident(data, session) {
  const payload = [{
    campus_id: session.campusId,
    resident_first_name: data.resident_first_name,
    resident_last_name: data.resident_last_name,
    resident_nickname: data.resident_nickname || '',
    resident_phone: (data.resident_phone || '').replaceAll('-', ''),
    resident_email: (data.resident_email || '').toLowerCase(),
    is_area_assigned: true,
    area_id: data.area_id,
    resident_group_ids: data.resident_group_ids || [],
    /* Optional. Left out entirely when empty: residentAdd fills in UTC now only when
       the key is ABSENT (rec.get default), and would pass '' or null on to
       sp_addResident. */
    ...(data.move_in_date ? { move_in_date: data.move_in_date } : {}),
    ehr_resident_id: data.ehr_resident_id || '',
    vendor_ids: session.vendor_ids,
    /* Duplicate check (docs/DUPLICATE_RESIDENT_DETECTION_PLAN.md §2.2). residentAdd
       refuses with Status "Duplicate" when the name matches a resident or pending EHR
       move-in task whose id is not listed here, i.e. one the user has not been shown.
       confirm_duplicate marks the audit row as "added despite a match". */
    ...(data.confirm_duplicate ? {
      confirm_duplicate: true,
      confirm_duplicate_ids: data.confirm_duplicate_ids || [],
      confirm_duplicate_task_ids: data.confirm_duplicate_task_ids || [],
    } : {}),
  }];

  return apiTransaction({
    transType: 'resident_add',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Edit an existing resident.
 * V1 TransType: resident_edit
 *
 * @param {object} data — Updated resident data
 * @param {object} oldData — Original resident data (for change tracking)
 * @param {object} session
 * @returns {Promise<[boolean, string?]>}
 */
export function editResident(data, oldData, session) {
  const payload = [{
    resident_id: parseInt(data.resident_id),
    resident_first_name: data.resident_first_name,
    resident_last_name: data.resident_last_name,
    resident_nickname: data.resident_nickname || '',
    is_active: 1,
    resident_email: (data.resident_email || '').toLowerCase(),
    resident_phone: (data.resident_phone || '').replaceAll('-', ''),
    area_id: parseInt(data.area_id),
    resident_group_ids: data.resident_group_ids || [],
    campus_id: session.campusId,
    ehr_resident_id: data.ehr_resident_id || '',
    /* ALWAYS sent. sp_editResident sets move_in_date = its argument unconditionally,
       so leaving it out (as this payload used to) wrote NULL over the stored date on
       every edit. The drawer echoes the stored value back when the date is untouched;
       null clears it. */
    move_in_date: data.move_in_date || null,
    vendor_ids: session.vendor_ids,
  }];

  const oldPayload = [{
    resident_id: parseInt(oldData.resident_id),
    resident_first_name: oldData.resident_first_name,
    resident_last_name: oldData.resident_last_name,
    resident_nickname: oldData.resident_nickname || '',
    is_active: oldData.is_active ? 1 : 0,
    resident_email: oldData.resident_email || '',
    resident_phone: oldData.resident_phone || '',
    area_id: oldData.area_id,
    resident_group_ids: oldData.resident_group_ids || [],
    campus_id: oldData.campus_id || session.campusId,
    ehr_resident_id: oldData.ehr_resident_id || '',
    move_in_date: oldData.move_in_date || null,
  }];

  return apiTransaction({
    transType: 'resident_edit',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Move a moved-out resident back in (re-activate).
 * TransType: resident_move_in_profile → residentMoveIn, which sets area_id and
 * is_active, clears move_out_date and leaves move_in_date as it was. Groups and
 * devices are not restored. Used by the resident profile's Move Back In and by the
 * Residents page's duplicate dialog.
 *
 * @param {Array<object>} payload — [{ resident_id, area_id, is_active: 1, modified_by,
 *   campus_id, vendor_ids, move_in_date }]
 * @param {Array<object>} oldPayload — [{ resident_id, area_id, is_active: 0, move_out_date }]
 *   (move_out_date is the "before" value for the log)
 * @param {object} session
 * @returns {Promise<[boolean, string?]>}
 */
export function moveInResident(payload, oldPayload, session) {
  return apiTransaction({
    transType: 'resident_move_in_profile',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Move out (disable) one or more residents.
 * V1 TransType: resident_disable
 *
 * @param {Array<number>} residentIds
 * @param {object} session
 * @returns {Promise<[boolean, string?]>}
 */
export function moveOutResidents(residentIds, session) {
  const payload = residentIds.map((id) => ({
    resident_id: id,
    vendor_ids: session.vendor_ids,
  }));

  return apiTransaction({
    transType: 'resident_disable',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Bulk-set resident group membership for several residents at once.
 *
 * V1 TransType: resident_edit_groups
 *
 * The backend calls sp_tlGroupResident per resident with the FULL new group id
 * list (it replaces membership), and skips any record whose
 * `resident_group_ids` is unchanged vs oldPayload. Ids are sorted on both sides
 * because that comparison is order-sensitive — sorting keeps "unchanged" records
 * reliably skipped instead of re-written.
 *
 * `resident_group_category_id` is intentionally omitted, mirroring the
 * single-resident edit path (the backend defaults it to 0).
 *
 * @param {Array<{resident_id: number, groupIds: number[], oldGroupIds: number[]}>} changes
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function bulkEditResidentGroups(changes, session) {
  const sortIds = (ids) => [...new Set((ids || []).map((id) => parseInt(id, 10)))]
    .filter((id) => !Number.isNaN(id))
    .sort((a, b) => a - b);

  const payload = changes.map((c) => ({
    resident_id: parseInt(c.resident_id, 10),
    campus_id: session.campusId,
    resident_group_ids: sortIds(c.groupIds),
  }));

  const oldPayload = changes.map((c) => ({
    resident_id: parseInt(c.resident_id, 10),
    campus_id: session.campusId,
    resident_group_ids: sortIds(c.oldGroupIds),
  }));

  return apiTransaction({
    transType: 'resident_edit_groups',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Relocate a resident to a different area.
 *
 * V1 TransType: resident_move_profile (same handler the resident profile uses —
 * there is no separate list-page variant).
 *
 * @param {number} residentId
 * @param {number} newAreaId — destination area
 * @param {number} currentAreaId — area being vacated (sent as oldPayload so the
 *        backend can diff the move and notify offsite vendors)
 * @param {object} session — { campusId, userId, clientId, vendor_ids }
 * @returns {Promise<[boolean, string?]>}
 */
export function relocateResident(residentId, newAreaId, currentAreaId, session) {
  return apiTransaction({
    transType: 'resident_move_profile',
    payload: [{
      resident_id: residentId,
      area_id: parseInt(newAreaId, 10),
      modified_by: session.userId,
      vendor_ids: session.vendor_ids,
    }],
    oldPayload: [{
      resident_id: residentId,
      area_id: currentAreaId,
    }],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Permanently delete one or more residents.
 * V1 TransType: resident_delete
 *
 * @param {Array<number>} residentIds
 * @param {object} session
 * @returns {Promise<[boolean, string?]>}
 */
export function deleteResidents(residentIds, session) {
  const payload = residentIds.map((id) => ({
    resident_id: parseInt(id),
  }));

  return apiTransaction({
    transType: 'resident_delete',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

// ---------------------------------------------------------------------------
// DEVICE operations
// ---------------------------------------------------------------------------

/**
 * Assign a device to a resident.
 * V1 TransType: device_assign_resident (Residents.jsx lines 1498-1535)
 *
 * @param {object} device — Full device record from fetchDevices (sp_dev_getInovonicsDevices)
 * @param {number} residentId — The resident to assign to
 * @param {object} session — { campusId, userId, clientId, vendor_ids }
 * @returns {Promise<[boolean, string?]>}
 */
export function assignDevice(device, residentId, session, residentAreaId) {
  const payload = [{
    device_id: device.device_id,
    campus_id: session.campusId,
    is_active: true,
    resident_id: parseInt(residentId),
    device_name: device.device_name,
    manufacture_model_id: parseInt(device.manufacture_model_id),
    manufacture_id: device.manufacture_id,
    /* The offsite vendor packagers read this off the record to identify the
       physical device — packageResidentUCC sends it as `manufacture_uuid`
       (mysql_transaction.py:~4135). Without it they receive a null serial. */
    manufacture_uuid: device.manufacture_uuid,
    resident_area_id: residentAreaId || 0,
    device_use_id: parseInt(device.device_use_id),
    descriptions: device.descriptions || '',
    is_resident: true,
    is_area_device: false,
    area_id: 0,
    device_type_id: device.device_type_id,
    modified_by: session.userId,
    vendor_ids: session.vendor_ids,
  }];

  const oldPayload = [{
    device_id: device.device_id,
    campus_id: device.campus_id,
    is_active: device.is_active,
    resident_id: device.resident_id,
    device_name: device.device_name,
    manufacture_model_id: device.manufacture_model_id,
    /* Needed here too: on a reassign the handler calls the packager a second
       time with oldPayload to tell the vendor to unassign from the previous
       holder, and that call identifies the device by serial. */
    manufacture_uuid: device.manufacture_uuid,
    device_use_id: device.device_use_id,
    descriptions: device.descriptions,
    is_resident: device.is_resident,
    is_area_device: device.is_area_device,
    area_id: device.area_id,
    device_type_id: device.device_type_id,
  }];

  return apiTransaction({
    transType: 'device_assign_resident',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Unassign (remove) a device from a resident.
 * V1 TransType: device_unassign_resident (Residents.jsx lines 997-1019)
 *
 * @param {number} deviceId — The device to unassign
 * @param {number} residentId — The resident it's currently assigned to
 * @param {object} session — { campusId, userId, clientId, vendor_ids }
 * @param {string|null} deviceName — Device name (used by Inovonics provisioning audit)
 * @returns {Promise<[boolean, string?]>}
 */
export function unassignDevice(deviceId, residentId, session, deviceName = null, manufactureId = 1) {
  const payload = [{
    device_id: deviceId,
    device_name: deviceName,
    is_active: false,
    resident_id: null,
    modified_by: session.userId,
    manufacture_id: manufactureId,
    vendor_ids: session.vendor_ids,
  }];

  const oldPayload = [{
    device_id: deviceId,
    resident_id: residentId,
  }];

  return apiTransaction({
    transType: 'device_unassign_resident',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}
